/**
 * ⚠️ CE COMPOSANT EXISTE EN DEUX EXEMPLAIRES.
 *
 *   front/process/src/components/PdfViewer.tsx  - parcours signataire
 *   front/app/src/components/PdfViewer.tsx      - portail, editeur de placement
 *
 * La copie est VOLONTAIRE (« on copie, on ne partage pas ») : les deux
 * fronts sont deux applications construites et deployees separement, et
 * personne ne doit pouvoir casser le parcours de signature en retouchant
 * l'editeur de placement.
 *
 * Elles ont depuis diverge PAR LEUR USAGE, et c'est legitime : l'une
 * expose un calque et le nombre de pages pour l'editeur, l'autre le suivi
 * des pages vues (B-18) et ses libelles.
 *
 * B-19 (20/09/2026) : LA DIVERGENCE EST DEVENUE STRUCTURELLE. Celle du
 * parcours signataire empile toutes les pages en defilement continu ;
 * CELLE-CI montre une page a la fois, et doit le rester - l'editeur pose
 * des cadres sur une page precise, avec un calque cale sur elle. Le
 * dimensionnement differe aussi, et ce n'est pas un oubli : ici un zoom
 * ABSOLU, ramene par `max-width: 100%` avec une hauteur en `auto`, et un
 * ResizeObserver sur le canvas lui-meme ; la-bas une echelle qui part de
 * la largeur disponible, mesuree sur un temoin.
 *
 * Verifie le 20/09/2026 : le defaut de mesure trouve la-bas - un
 * ResizeObserver pose sur le CONTENEUR ne signale pas l'apparition de sa
 * propre barre de defilement, la boite de bordure ne bougeant pas - ne
 * s'applique PAS ici, ou l'observateur regarde le canvas, dont la bordure
 * retrecit bien avec `max-width`.
 *
 * Le coeur commun se reduit desormais a : configuration du worker PDF.js,
 * chargement du document, densite d'ecran, annulation du rendu en cours.
 *
 * ⚠️ UN CORRECTIF SUR CE COEUR-LA DOIT ETRE VERIFIE DANS L'AUTRE COPIE.
 *
 * Ce que cela a deja coute, et pourquoi cette note existe : le 18/09/2026,
 * le portail a corrige une page ECRASEE (largeur ramenee par le CSS,
 * hauteur laissee en style en ligne). Le correctif n'a pas ete porte. Le
 * 20/09, un signataire reel a trouve le defaut sur son telephone - mesure
 * a 375 px : rapport affiche 0,285 pour un A4 a 0,707. Deux jours, et un
 * defaut deja resolu a dix metres de la.
 *
 * La duplication ne fait pas mal quand on ECRIT le code - les deux
 * fichiers sont alors identiques. Elle fait mal quand on le CORRIGE, des
 * mois plus tard, et rien dans le fichier qu'on modifie ne rappelle que
 * l'autre existe. D'ou cette note, ici, en tete.
 */
/**
 * Visionneuse PDF du portail client (B-08 lot 3.1, decision du porteur du
 * 17/09/2026 : avant l'envoi, le client voit son document et peut le
 * telecharger).
 *
 * Copie adaptee de `front/process/src/components/PdfViewer.tsx` : la
 * convention des fronts est de COPIER la brique plutot que de partager un
 * paquet (cf. front/app/README.md). Trois differences avec celle du
 * parcours signataire : libelles en francais en dur (le portail n'est pas
 * traduit), pas de bouton « imprimer », et un CALQUE optionnel au-dessus de
 * la page - c'est par lui que l'editeur de placement (lot 3.2) posera les
 * cadres, sans avoir a rouvrir cette visionneuse.
 *
 * ⚠️ Jamais de visionneuse distante (Google Docs Viewer et equivalents) :
 * cela enverrait le document confidentiel de notre client chez un tiers.
 * PDF.js est empaquete avec l'application et servi depuis notre domaine.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import * as pdfjs from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";

// Le worker est resolu par Vite depuis node_modules et emis dans notre
// bundle : aucun appel reseau vers un CDN.
//
// workerSrc et NON workerPort : un `workerPort` est un worker UNIQUE et
// partage, que le premier `destroy()` detruit definitivement - tout rendu
// ulterieur echoue alors sur "the worker is being destroyed" et l'ecran
// reste blanc. Avec workerSrc, PDF.js cree et detruit un worker par
// document, ce qui rend le cycle de vie correct.
import workerUrl from "pdfjs-dist/build/pdf.worker.mjs?url";

// Le suffixe n'est pas decoratif. Jusqu'au 17/09/2026, nginx servait les
// .mjs en « application/octet-stream » ; avec « nosniff », le navigateur
// refuse alors de les executer et PDF.js bascule sur un worker de secours
// qui echoue. Le type MIME a ete corrige, mais les navigateurs qui avaient
// deja visite le site gardent la mauvaise reponse en cache UN AN (actifs
// servis en « immutable ») et le nom du fichier ne change pas puisqu'il
// derive de son contenu. Changer l'URL est le seul moyen de les liberer.
// A incrementer si le cas se represente - et a garder identique a celui du
// parcours signataire.
pdfjs.GlobalWorkerOptions.workerSrc = `${workerUrl}?mime=2`;

/** Taille AFFICHEE de la page, mesuree sur le canvas (pas calculee depuis
 *  le viewport PDF.js) : c'est le repere dans lequel le calque de placement
 *  travaille.
 *
 *  Pourquoi mesuree et non calculee : quand la page rendue est plus large
 *  que la zone disponible, `max-width: 100%` la reduit APRES coup. Un
 *  calque cale sur la taille calculee se decalait alors - verifie le
 *  18/09/2026, un cadre pose a 10 % de la largeur se retrouvait a 12,17 %
 *  apres deux crans de zoom. */
export interface TaillePage {
  page: number;
  largeur: number;
  hauteur: number;
}

interface Props {
  data: ArrayBuffer;
  onTelecharger: () => void;
  /** Calque dessine au-dessus de la page affichee (editeur de placement). */
  calque?: (taille: TaillePage) => ReactNode;
  /** Nombre de pages du document, des qu'il est lu : un modele de placement
   *  ne peut etre applique qu'en le connaissant. */
  onPages?: (nombre: number) => void;
}

function IconeTelecharger() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
      <path d="M8 2v7m0 0 3-3M8 9 5 6" fill="none" stroke="currentColor" strokeWidth="1.4"
            strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2.5 11.5v1.2a.8.8 0 0 0 .8.8h9.4a.8.8 0 0 0 .8-.8v-1.2" fill="none"
            stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;

export function PdfViewer({ data, onTelecharger, calque, onPages }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  // Un rendu PDF.js en cours doit etre annule avant d'en lancer un autre
  // sur le meme canvas, sinon les deux se marchent dessus et la page
  // s'affiche a moitie.
  const renderTaskRef = useRef<{ cancel: () => void } | null>(null);

  // Le document est un ETAT et non une ref : l'effet de rendu doit se
  // redeclencher quand il change. En ref, l'effet ne dependait que du
  // NOMBRE de pages, donc remplacer un PDF par un autre de meme longueur
  // laissait l'ancien rendu a l'ecran - et en developpement (StrictMode,
  // qui rejoue les effets), la page ne s'affichait plus du tout.
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const pageCount = doc?.numPages ?? 0;
  const [pageNo, setPageNo] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [isFullscreen, setFullscreen] = useState(false);
  const [failed, setFailed] = useState(false);
  const [taille, setTaille] = useState<TaillePage | null>(null);

  useEffect(() => {
    let cancelled = false;
    // PDF.js prend possession du tampon (transferable) et le neutralise :
    // on lui donne une copie, sinon un second rendu - ou le
    // telechargement - travaillerait sur un tampon vide.
    const task = pdfjs.getDocument({ data: data.slice(0) });
    task.promise.then(
      (doc) => {
        if (cancelled) {
          void doc.destroy();
          return;
        }
        setDoc(doc);
        setPageNo(1);
        setTaille(null);
        onPages?.(doc.numPages);
      },
      () => {
        if (!cancelled) setFailed(true);
      },
    );
    return () => {
      cancelled = true;
      void task.destroy();
      setDoc(null);
    };
  }, [data]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!doc || !canvas) return;

    let cancelled = false;
    void (async () => {
      const page = await doc.getPage(pageNo);
      if (cancelled) return;

      // Rendu a la densite reelle de l'ecran : sans cela le texte est flou
      // sur ecran Retina, ce qui donne l'impression d'un document de
      // mauvaise qualite.
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({ scale: scale * dpr });
      const context = canvas.getContext("2d");
      if (!context) return;

      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${viewport.width / dpr}px`;
      // Hauteur en `auto` et NON en pixels : quand la page rendue est plus
      // large que la zone disponible, `max-width: 100%` reduit la largeur,
      // mais une hauteur posee en style en ligne, elle, ne bougerait pas -
      // la page serait etiree verticalement, et le calque ne couvrirait
      // plus le document.
      canvas.style.height = "auto";

      renderTaskRef.current?.cancel();
      const task = page.render({ canvasContext: context, viewport });
      renderTaskRef.current = task;
      try {
        await task.promise;
        if (!cancelled) {
          const r = canvas.getBoundingClientRect();
          setTaille({ page: pageNo, largeur: r.width, hauteur: r.height });
        }
      } catch {
        // Annulation volontaire lors d'un changement rapide de page ou de
        // zoom : ce n'est pas une erreur a remonter.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [doc, pageNo, scale]);

  // La largeur affichee change aussi sans nouveau rendu : fenetre
  // redimensionnee, passage en plein ecran, apparition d'une barre de
  // defilement. Sans cela le calque garderait la taille du dernier rendu et
  // les cadres se decaleraient.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === "undefined") return;
    const obs = new ResizeObserver(() => {
      const r = canvas.getBoundingClientRect();
      if (r.width <= 0) return;
      // Ne remplacer l'etat QUE si la taille a vraiment change : sans cette
      // comparaison, chaque notification produit un nouvel objet, donc un
      // nouveau rendu React, qui renotifie - la page tourne en rond.
      setTaille((t) =>
        t && Math.abs(t.largeur - r.width) < 0.5 && Math.abs(t.hauteur - r.height) < 0.5
          ? t
          : t
            ? { ...t, largeur: r.width, hauteur: r.height }
            : t,
      );
    });
    obs.observe(canvas);
    return () => obs.disconnect();
  }, [doc]);

  useEffect(() => {
    const onChange = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void shellRef.current?.requestFullscreen?.().catch(() => undefined);
    }
  }, []);

  if (failed) {
    return (
      <div className="alert alert--error" role="alert">
        Ce document ne peut pas être affiché ici. Vous pouvez le télécharger pour le
        consulter.
        <div className="actions">
          <button type="button" className="btn btn--secondary" onClick={onTelecharger}>
            Télécharger
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="viewer" ref={shellRef}>
      <div className="viewer__bar">
        <button
          type="button"
          className="icon-btn"
          onClick={() => setPageNo((n) => Math.max(1, n - 1))}
          disabled={pageNo <= 1}
          aria-label="Page précédente"
        >
          ‹
        </button>
        {/* Deux formes du meme indicateur : la longue sur grand ecran, la
            courte sur mobile - une seule est affichee, donc une seule est
            lue par un lecteur d'ecran. */}
        <span aria-live="polite">
          <span className="viewer__page-long">
            Page {pageNo} sur {pageCount || "…"}
          </span>
          <span className="viewer__page-court">
            {pageNo} / {pageCount || "…"}
          </span>
        </span>
        <button
          type="button"
          className="icon-btn"
          onClick={() => setPageNo((n) => Math.min(pageCount, n + 1))}
          disabled={pageNo >= pageCount}
          aria-label="Page suivante"
        >
          ›
        </button>

        <span className="viewer__spacer" />

        <button
          type="button"
          className="icon-btn"
          onClick={() => setScale((s) => Math.max(MIN_SCALE, +(s - 0.2).toFixed(2)))}
          disabled={scale <= MIN_SCALE}
          aria-label="Réduire"
        >
          −
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={() => setScale((s) => Math.min(MAX_SCALE, +(s + 0.2).toFixed(2)))}
          disabled={scale >= MAX_SCALE}
          aria-label="Agrandir"
        >
          +
        </button>
        <button
          type="button"
          className="icon-btn viewer__plein-ecran"
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
        >
          ⛶
        </button>
        <button type="button" className="icon-btn viewer__action" onClick={onTelecharger}
                aria-label="Télécharger">
          <IconeTelecharger />
          <span className="viewer__action-texte">Télécharger</span>
        </button>
      </div>

      <div className="viewer__scroll">
        {/* Le bloc epouse le canvas (inline-block, aucune taille imposee) :
            le calque, en `inset: 0`, couvre donc EXACTEMENT la page
            affichee, zoom compris. */}
        <div className="viewer__page">
          <canvas ref={canvasRef} className="viewer__canvas" />
          {calque && taille && taille.page === pageNo ? (
            <div className="viewer__calque">{calque(taille)}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
